import { useMemo } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { PlusIcon } from "@icons";
import {
    buildDataSections,
    buildFlatItems,
    buildSuggestions,
    plainFlatItemOptions as plainOptions,
} from "./PickerPlayground.lib";
import { buildTreeItems } from "./PickerPlaygroundTree";
import { VariantGrid, VariantItem } from "./playgroundControls";
import styles from "./PickerPlayground.module.scss";

const decoratedOptions = {
    hasDisabledItems: false,
    hasDestructiveItem: false,
    hasDescriptions: true,
    hasRightText: true,
};

/**
 * Text-trigger cells: closed triggers show the resting input and button
 * trigger states, inline roots show the list states (loading, error, load
 * more, suggestions, tree, sections, creatable footer) without opening a popup.
 */
export const PickerRootsGrid = () => {
    const items = useMemo(() => buildFlatItems(plainOptions), []);
    const decorated = useMemo(() => buildFlatItems(decoratedOptions), []);
    const sections = useMemo(() => buildDataSections(plainOptions), []);
    const tree = useMemo(
        () => buildTreeItems({ hasDescriptions: false, hasRightText: false }),
        [],
    );
    const suggestions = useMemo(() => buildSuggestions(), []);

    return (
        <VariantGrid title="Text triggers and custom values" columns="wide">
            <VariantItem label='Select · trigger="input"'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        items={items}
                        trigger="input"
                        clearable
                        defaultValue={items[1].value}
                        placeholder="Select account"
                    />
                </div>
            </VariantItem>
            <VariantItem label='Select · trigger="input" · multiple, 5 selected'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        multiple
                        items={items}
                        trigger="input"
                        selectionSummary={{ mode: "list" }}
                        defaultValue={items.map((item) => item.value)}
                        placeholder="Select accounts"
                    />
                </div>
            </VariantItem>
            <VariantItem label='Select · trigger="input" · filterable={false}'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        items={items}
                        trigger="input"
                        filterable={false}
                        defaultValue={items[1].value}
                        placeholder="Select account"
                        clearable
                    />
                </div>
            </VariantItem>
            <VariantItem label='Select · trigger="input" · multiple'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        multiple
                        trigger="input"
                        items={items}
                        defaultValue={[items[0].value, items[1].value]}
                        placeholder="Select accounts"
                    />
                </div>
            </VariantItem>
            <VariantItem label="Select · searchable (popup search)">
                <div className={styles.matrixCell}>
                    <Picker.Select
                        items={items}
                        searchable
                        defaultValue={items[1].value}
                        placeholder="Select account"
                    />
                </div>
            </VariantItem>
            <VariantItem label='Inline · status="loading"'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        items={items}
                        status="loading"
                        loadingLabel="Loading accounts"
                    />
                </div>
            </VariantItem>
            <VariantItem label='Inline · status="error"'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        items={items}
                        status="error"
                        errorLabel="Accounts could not be loaded"
                    />
                </div>
            </VariantItem>
            <VariantItem label='Inline · hasMore · loadMoreStatus="loading"'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        items={items}
                        hasMore
                        loadMoreStatus="loading"
                    />
                </div>
            </VariantItem>
            <VariantItem label='Inline · hasMore · loadMoreStatus="error" · Try again row'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        items={items}
                        hasMore
                        loadMoreStatus="error"
                    />
                </div>
            </VariantItem>
            <VariantItem label="allowCustomValue · suggestions">
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        trigger="input"
                        allowCustomValue
                        items={suggestions}
                        defaultValue="Co"
                        placeholder="Describe the expense"
                    />
                </div>
            </VariantItem>
            <VariantItem label="allowCustomValue · description and right text">
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        trigger="input"
                        allowCustomValue
                        items={decorated}
                        placeholder="Describe the expense"
                    />
                </div>
            </VariantItem>
            <VariantItem label="allowCustomValue · tree">
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        trigger="input"
                        allowCustomValue
                        items={tree}
                        defaultValue="el"
                        placeholder="Describe the expense"
                    />
                </div>
            </VariantItem>
            <VariantItem label="allowCustomValue · sections">
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        trigger="input"
                        allowCustomValue
                        sections={sections}
                        placeholder="Describe the expense"
                    />
                </div>
            </VariantItem>
            <VariantItem label='allowCustomValue · Use "query" footer'>
                <div className={styles.matrixCell}>
                    <Picker.Select
                        inline
                        trigger="input"
                        allowCustomValue
                        items={suggestions}
                        defaultValue="Fo"
                        emptyBehavior="hide"
                        placeholder="Describe the expense"
                        renderFooter={(query) => (
                            <Picker.Item
                                leading={<PlusIcon />}
                                onSelect={() => undefined}
                            >
                                {`Use "${query}"`}
                            </Picker.Item>
                        )}
                    />
                </div>
            </VariantItem>
        </VariantGrid>
    );
};
